/**
 * The `@` picker's decisions, as pure functions.
 *
 * One trigger searches both indexes — what this document defines, and the
 * author's library — and emits whichever command the chosen target needs. The
 * author never has to know whether they wanted a citation or a
 * cross-reference; making them type `@cite{` or `@ref{` was our data model in
 * their sentence. (Olivia, SPEC-references §1–2.)
 *
 * Kept free of React and ProseMirror so every rule below is a test, not a
 * screenshot.
 */

import type { ReferenceForm } from "@/lib/tiptap/extensions/reference";

/** A label this document defines — the shape GET /references returns. */
export interface DocumentTarget {
  key: string;
  kind: string;
  caption: string | null;
  /** What `\ref` prints, from the last compile. Null when never compiled. */
  number: string | null;
}

/** A bibliography entry. */
export interface LibraryEntry {
  citeKey: string;
  author?: string;
  year?: string;
  title?: string;
}

export type PickerFilter = "all" | "document" | "library";

export interface Trigger {
  query: string;
  filter: PickerFilter;
  /** Characters to replace, counted back from the cursor. */
  length: number;
}

export type Row =
  | { group: "document"; key: string; number: string | null; label: string; kind: string }
  | { group: "library"; key: string; number: null; label: string; kind: "citation" };

/**
 * What the author has typed, if it is a picker trigger.
 *
 * `@` opens it only at the start of a word — after a space, an opening bracket
 * or the start of the line — so an email address in prose does not. The query
 * runs to the cursor and stops at whitespace: `@results` is a search, `@ results`
 * is a sentence.
 *
 * `@cite{` and `@ref{` still work, as filters on this picker rather than as
 * separate doors, and `\cite{` keeps working because authors who know LaTeX
 * type it.
 */
export function parseTrigger(textBefore: string): Trigger | null {
  const braced = textBefore.match(/(?:^|[\s([])((?:@|\\)(cite|ref)\{([^}\s]*))$/);
  if (braced) {
    const [, whole, which, query] = braced;
    if (whole.startsWith("\\") && which === "ref") return null;
    return { query, filter: which === "cite" ? "library" : "document", length: whole.length };
  }

  const bare = textBefore.match(/(?:^|[\s([])(@([^\s@{}]*))$/);
  if (bare) {
    const [, whole, query] = bare;
    return { query, filter: "all", length: whole.length };
  }

  return null;
}

/**
 * How well a row's text matches the query. Caption first, key as the
 * fallback — authors search for what the thing *is*, and `tab:results` is our
 * identifier, not theirs. Zero means no match.
 */
function score(query: string, label: string, key: string): number {
  if (!query) return 1;
  const q = query.toLowerCase();
  const l = label.toLowerCase();
  if (l.startsWith(q)) return 4;
  if (l.split(/[\s\-–—:,.]+/).some((w) => w.startsWith(q))) return 3;
  if (l.includes(q)) return 2;
  if (key.toLowerCase().includes(q)) return 1;
  return 0;
}

function libraryLabel(e: LibraryEntry): string {
  const who = e.author?.split(/\s+and\s+/i)[0]?.split(",")[0]?.trim();
  const head = [who, e.year].filter(Boolean).join(" ");
  return [head, e.title].filter(Boolean).join(" · ") || e.citeKey;
}

/**
 * The two groups, ranked. Document targets are returned separately and a
 * caller must list them first: pointing inside your own paper is the commoner
 * act, so it outranks the library regardless of match score.
 *
 * Un-numbered targets are offered, not hidden or greyed — the author knows the
 * equation exists, and hiding it is the worst option available.
 */
export function buildRows(input: {
  targets: DocumentTarget[];
  library: LibraryEntry[];
  query: string;
  filter: PickerFilter;
}): { document: Row[]; library: Row[] } {
  const { targets, library, query, filter } = input;

  const rank = <T extends Row>(rows: { row: T; s: number }[]) =>
    rows.filter((r) => r.s > 0).sort((a, b) => b.s - a.s).map((r) => r.row);

  const document =
    filter === "library"
      ? []
      : rank(
          targets.map((t) => {
            const label = t.caption?.trim() || t.key;
            return {
              row: { group: "document" as const, key: t.key, number: t.number, label, kind: t.kind },
              s: score(query, label, t.key),
            };
          }),
        );

  const lib =
    filter === "document"
      ? []
      : rank(
          library.map((e) => {
            const label = libraryLabel(e);
            return {
              row: { group: "library" as const, key: e.citeKey, number: null, label, kind: "citation" as const },
              s: score(query, label, e.citeKey),
            };
          }),
        );

  return { document, library: lib };
}

/** Document first, always — the order the keyboard walks. */
export function flatten(groups: { document: Row[]; library: Row[] }): Row[] {
  return [...groups.document, ...groups.library];
}

/**
 * The command a document target is written with. `\eqref` for an equation,
 * because it prints the parentheses equations are cited with; `\cref`
 * otherwise, because it writes "Table 3" rather than a bare "3". cleveref is
 * in Lilia's standard preamble. A document whose preamble lacks it is the
 * picker's later job — Olivia's spec has the row offer to add it.
 */
export function formFor(row: { kind: string }): ReferenceForm {
  return row.kind === "equation" ? "eqref" : "cref";
}

/** The slot a missing number shows. Never `?` — LaTeX prints `??` for a
 *  broken reference, and a pending one must not look like that. */
export const PENDING_SLOT = "▯";
